import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { useStudents } from 'context/StudentsContext';
import { useIEPGoals } from 'context/IEPGoalsContext';
import { useIncidents } from 'context/IncidentsContext';
import IepProgressReport from '../reports-and-analytics/components/reports/IepProgressReport';
import BehaviorTrendsReport from '../reports-and-analytics/components/reports/BehaviorTrendsReport';
import DataCollectionReport from '../reports-and-analytics/components/reports/DataCollectionReport';
import ComplianceReport from '../reports-and-analytics/components/reports/ComplianceReport';
import { formatDateRangeLabel } from '../reports-and-analytics/reportUtils';
import { useCurrentUser } from '../../lib/currentUser';

// ---------------------------------------------------------------------------
// PrintReports — dedicated print-first route for the four reports.
//
// The Reports & Analytics page lives inside the usual app shell (Header +
// Sidebar + tab strip), which is great on screen but wastes ink and
// pixels on paper / PDF. This route strips all that chrome and renders
// only the selected report in a print-optimized wrapper. Every chart is
// still rendered by Recharts (SVG), so "Save as PDF" from the browser's
// print dialog produces a real vector PDF that scales cleanly at any
// zoom level.
//
// Route:   /print/reports/:reportType
// Params:  from, to, students (comma-separated ids), autoprint=1
//
// When `autoprint=1` is set, we call `window.print()` after the report
// has had a chance to render (charts need one animation-frame to size
// themselves inside ResponsiveContainer). Users who visit the URL
// without `autoprint` see the preview and click Print themselves —
// useful for testing and for bookmarks.
// ---------------------------------------------------------------------------

const REPORT_META = {
  'iep-progress': {
    title: 'IEP Progress — Snapshot',
    subtitle:
      "Current status of every goal on the caseload. Numbers reflect each goal's most recent value from the database at the moment of print.",
    Component: IepProgressReport,
  },
  'behavior-trends': {
    title: 'Behavior Trends',
    subtitle:
      'Behavior incidents rolled up over the selected window — count over time, intensity distribution, and per-student totals.',
    Component: BehaviorTrendsReport,
  },
  'data-collection': {
    title: 'Data Collection Cadence',
    subtitle:
      'How consistently the team is logging progress data. Stale goals (no entries in 14+ days) surface first.',
    Component: DataCollectionReport,
  },
  compliance: {
    title: 'IEP Compliance',
    subtitle:
      'Federal IDEA requires an annual IEP review. This report bins every student by how far away their next review is.',
    Component: ComplianceReport,
  },
};

const PrintReports = () => {
  const navigate = useNavigate();
  const { reportType } = useParams();
  const [searchParams] = useSearchParams();
  const { students } = useStudents();
  const { goals } = useIEPGoals();
  const { incidents } = useIncidents();
  const currentUser = useCurrentUser();

  // Parse filters from URL. `from`/`to` are yyyy-mm-dd; `students` is a
  // comma-separated list of student ids. Missing filters imply "all".
  const dateRange = useMemo(() => {
    const from = searchParams.get('from') || null;
    const to = searchParams.get('to') || null;
    if (!from && !to) return { from: null, to: null };
    return { from, to };
  }, [searchParams]);

  const studentFilter = useMemo(() => {
    const raw = searchParams.get('students');
    if (!raw) return new Set();
    return new Set(
      raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    );
  }, [searchParams]);

  const shouldAutoPrint = searchParams.get('autoprint') === '1';

  const meta = REPORT_META[reportType];
  const Component = meta?.Component;

  // Track whether the data we need for THIS report is loaded, so we can
  // delay the auto-print until charts have something to draw. Each
  // report needs different data:
  //   * iep-progress   → students + goals
  //   * behavior-trends → students + incidents
  //   * data-collection → students + goals (progress entries load internally)
  //   * compliance     → students + goals
  const isDataReady = useMemo(() => {
    if (!Array.isArray(students)) return false;
    if (reportType === 'behavior-trends') return Array.isArray(incidents);
    return Array.isArray(goals);
  }, [reportType, students, goals, incidents]);

  // Fire the auto-print exactly once after data is ready and the DOM
  // has settled. The delay covers ResponsiveContainer layout (~1 rAF),
  // font load, and the browser's own paint cycle. Data Collection also
  // has to internally fetch goal_progress_entries after mount, so it
  // gets an extra beat — otherwise the cadence chart can print empty
  // even though the entries are on their way. Users who need to wait
  // longer can hit the Print button manually.
  const hasPrintedRef = useRef(false);
  const [ready, setReady] = useState(false);
  const settleDelayMs = reportType === 'data-collection' ? 1400 : 700;

  useEffect(() => {
    if (!isDataReady || ready) return;
    const t = setTimeout(() => setReady(true), settleDelayMs);
    return () => clearTimeout(t);
  }, [isDataReady, ready, settleDelayMs]);

  useEffect(() => {
    if (!ready || !shouldAutoPrint || hasPrintedRef.current) return;
    hasPrintedRef.current = true;
    // A second RAF gives Recharts an extra frame to lay its SVGs out
    // at final size before the browser snapshots the page for print.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          window.print();
        } catch {
          // Print dialog was blocked or unsupported; fall through and
          // let the user click the Print button manually.
        }
      });
    });
  }, [ready, shouldAutoPrint]);

  // Unknown report type — show a friendly 404-ish state instead of
  // rendering an empty page.
  if (!meta) {
    return (
      <div className="min-h-screen bg-white text-gray-900 p-8 max-w-[8.5in] mx-auto">
        <h1 className="text-xl font-semibold mb-2">Report not found</h1>
        <p className="text-gray-600 mb-4">
          &quot;{reportType}&quot; isn&apos;t one of the built-in reports.
          Head back to Reports &amp; Analytics and pick a report from the
          tab strip, then click Print / Save as PDF.
        </p>
        <button
          type="button"
          onClick={() => navigate('/reports-and-analytics')}
          className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          <Icon name="ChevronLeft" size={14} />
          Back to Reports
        </button>
      </div>
    );
  }

  const filterSummary = describeFilters(dateRange, studentFilter, students);
  const generatedAt = new Date();

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white print-reports">
      {/* Print controls bar — visible on screen, hidden in the printed
          output via `.print:hidden`. */}
      <div className="print:hidden sticky top-0 z-10 border-b border-gray-200 bg-white">
        <div className="max-w-[8.5in] mx-auto flex items-center justify-between p-3">
          <button
            type="button"
            onClick={() => navigate('/reports-and-analytics')}
            className="inline-flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
          >
            <Icon name="ChevronLeft" size={16} />
            Back to Reports
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 hidden sm:inline">
              {shouldAutoPrint
                ? 'Print dialog opens automatically'
                : 'Preview mode'}
            </span>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md bg-primary text-white hover:bg-primary/90"
            >
              <Icon name="Printer" size={14} />
              Print / Save as PDF
            </button>
          </div>
        </div>
      </div>

      {/* Printable sheet — Letter width, generous margins on screen
          collapse to the browser's @page margins when printed. */}
      <div className="max-w-[8.5in] mx-auto bg-white p-8 print:p-0 print:max-w-none">
        <header className="border-b border-gray-300 pb-4 mb-6 print:break-after-avoid">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-gray-500 font-medium">
                EduTracker Pro · Report
              </p>
              <h1 className="text-xl font-bold text-gray-900 mt-1">
                {meta.title}
              </h1>
              <p className="text-sm text-gray-600 mt-1 max-w-2xl">
                {meta.subtitle}
              </p>
            </div>
            <div className="text-right text-[11px] text-gray-500 flex-shrink-0">
              <div>Generated {generatedAt.toLocaleString()}</div>
              {currentUser?.name && (
                <div>By {currentUser.name}</div>
              )}
            </div>
          </div>

          {/* Filter summary chips */}
          <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
            {filterSummary.map((chip) => (
              <span
                key={chip.label}
                className="inline-flex items-center gap-1 rounded-full border border-gray-300 bg-gray-50 px-2 py-0.5 text-gray-700"
              >
                <Icon name={chip.icon} size={11} className="text-gray-500" />
                {chip.label}
              </span>
            ))}
          </div>
        </header>

        {/* Loading state so the print dialog doesn't fire on an empty
            skeleton. The `.print:hidden` variants hide this from paper
            just in case a user prints via keyboard shortcut before
            data arrives — the report body still renders below. */}
        {!isDataReady && (
          <div className="print:hidden mb-4 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600 flex items-center gap-2">
            <Icon name="Loader" size={14} className="animate-spin" />
            Loading data…
          </div>
        )}

        {/* The actual report, in printMode so it hides the toolbar
            action buttons, kills chart animations, and applies
            break-inside: avoid on every chart card. */}
        <div className="print-report-body">
          <Component
            dateRange={dateRange}
            studentFilter={studentFilter}
            printMode
          />
        </div>

        {/* Footer with provenance so the printed sheet is
            self-documenting when it lives in a paper folder. */}
        <footer className="mt-8 pt-4 border-t border-gray-300 text-[10px] text-gray-500 print:break-before-avoid">
          <p>
            All numbers computed directly from the live database at the
            moment this report was generated. No cached aggregates, no
            synthetic examples — what appears here is what the
            database contained on {generatedAt.toLocaleString()}.
          </p>
          <p className="mt-1">
            EduTracker Pro · Reports · {meta.title}
          </p>
        </footer>
      </div>

      {/* Print styles. Uses letter paper with reasonable margins.
          `break-inside: avoid` on any element with `.print-avoid-break`
          keeps a chart from being cut in half between two pages.
          KpiCard / ChartCard already have `print:break-inside-avoid`
          from Tailwind's arbitrary-variant utility. */}
      <style>{`
        @media print {
          @page {
            size: letter;
            margin: 0.5in;
          }
          html, body {
            background: #fff !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:break-inside-avoid {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .print\\:break-after-avoid {
            break-after: avoid;
            page-break-after: avoid;
          }
          .print\\:break-before-avoid {
            break-before: avoid;
            page-break-before: avoid;
          }
          .print-reports {
            background: #fff !important;
          }
          /* Keep KPI grids together on the page when there's room. */
          .print-report-body .grid {
            break-inside: avoid;
          }
          /* Recharts renders SVG that scales fine at print zoom, but
             the containing div sometimes clips. Force overflow-visible
             so nothing gets cropped. */
          .print-report-body .recharts-wrapper,
          .print-report-body .recharts-surface {
            overflow: visible !important;
          }
          /* Slightly tighter tables for paper. */
          .print-report-body table {
            font-size: 11px;
          }
          .print-report-body th,
          .print-report-body td {
            padding: 4px 6px !important;
          }
        }
      `}</style>
    </div>
  );
};

// describeFilters — turn the URL params into a small list of chips the
// header renders. Kept out-of-component so it doesn't re-run on every
// state change.
function describeFilters(dateRange, studentFilter, students) {
  const chips = [];
  chips.push({
    icon: 'Calendar',
    label: `Range: ${formatDateRangeLabel(dateRange)}`,
  });

  const total = (Array.isArray(students) ? students : []).filter(
    (s) => (s.status || 'active') !== 'archived',
  ).length;

  if (!studentFilter || studentFilter.size === 0) {
    chips.push({
      icon: 'Users',
      label: `All active students${total ? ` (${total})` : ''}`,
    });
  } else {
    const named = (Array.isArray(students) ? students : [])
      .filter((s) => studentFilter.has(s.id))
      .map(
        (s) =>
          `${s.firstName || ''} ${s.lastName || ''}`.trim() ||
          'Unnamed student',
      );
    if (named.length <= 3) {
      chips.push({
        icon: 'Users',
        label: `Students: ${named.join(', ') || `${studentFilter.size} selected`}`,
      });
    } else {
      chips.push({
        icon: 'Users',
        label: `${named.length} students selected`,
      });
    }
  }
  return chips;
}

export default PrintReports;
